import fs from "node:fs/promises";
import path from "node:path";

export async function exists(filePath: string): Promise<boolean> {
  try {
    await fs.access(filePath);
    return true;
  } catch {
    return false;
  }
}

import { execSync } from "node:child_process";

/**
 * Safe wrapper for writing files that enforces branch guards on protected branches.
 * NOTE: This branch guard only protects write operations performed through this kit wrapper
 * (or the kit's runtime executions). It does not intercept external shell commands or processes.
 */
export async function writeFileSafe(filePath: string, content: string): Promise<void> {
  const absolutePath = path.resolve(filePath);
  const repoCwd = path.resolve(process.cwd());
  const relativeToRepo = path.relative(repoCwd, absolutePath).replaceAll("\\", "/");
  
  // Normalized precise allowlist
  const isOperational = relativeToRepo === "EVIDENCE.json" ||
                        relativeToRepo.startsWith(".ai-workflow/") ||
                        relativeToRepo.startsWith(".ai-workflow\\");
                        
  if (!isOperational) {
    let currentBranch = "";
    const fileCwd = path.dirname(absolutePath);
    try {
      currentBranch = execSync("git branch --show-current", { cwd: fileCwd, encoding: "utf8", stdio: "pipe" }).trim();
    } catch {
      // ignore
    }
    const protectedBranches = ["main", "master"];
    const isInitCommand = process.argv.includes("init");
    if (protectedBranches.includes(currentBranch) && !isInitCommand) {
      if (process.env.AI_WORKFLOW_BYPASS_PROTECTED_BRANCH !== "true") {
        throw new Error("FAIL_PROTECTED_BRANCH: Direct writes to protected branch are prohibited.");
      }
    }
  }
  await fs.mkdir(path.dirname(filePath), { recursive: true });
  await fs.writeFile(filePath, content, "utf8");
}

export async function readJson(filePath: string): Promise<any> {
  const content = await fs.readFile(filePath, "utf8");
  return JSON.parse(content);
}

function stripJsonComments(content: string): string {
  let output = "";
  let inString = false;
  let quote = "";
  let escaped = false;
  let inLineComment = false;
  let inBlockComment = false;

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];
    const next = content[index + 1];

    if (inLineComment) {
      if (char === "\n" || char === "\r") {
        inLineComment = false;
        output += char;
      }
      continue;
    }

    if (inBlockComment) {
      if (char === "*" && next === "/") {
        inBlockComment = false;
        index += 1;
        continue;
      }
      if (char === "\n" || char === "\r") {
        output += char;
      }
      continue;
    }

    if (inString) {
      output += char;
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === quote) {
        inString = false;
        quote = "";
      }
      continue;
    }

    if (char === '"' || char === "'") {
      inString = true;
      quote = char;
      output += char;
      continue;
    }

    if (char === "/" && next === "/") {
      inLineComment = true;
      index += 1;
      continue;
    }

    if (char === "/" && next === "*") {
      inBlockComment = true;
      index += 1;
      continue;
    }

    output += char;
  }

  return output;
}

function stripTrailingCommas(content: string): string {
  let output = "";
  let inString = false;
  let quote = "";
  let escaped = false;

  for (let index = 0; index < content.length; index += 1) {
    const char = content[index];

    if (inString) {
      output += char;
      if (escaped) {
        escaped = false;
      } else if (char === "\\") {
        escaped = true;
      } else if (char === quote) {
        inString = false;
        quote = "";
      }
      continue;
    }

    if (char === '"' || char === "'") {
      inString = true;
      quote = char;
      output += char;
      continue;
    }

    if (char === ",") {
      let nextIndex = index + 1;
      while (/\s/.test(content[nextIndex] ?? "")) {
        nextIndex += 1;
      }
      if (content[nextIndex] === "}" || content[nextIndex] === "]") {
        continue;
      }
    }

    output += char;
  }

  return output;
}

export async function readJsonc(filePath: string): Promise<any> {
  const content = await fs.readFile(filePath, "utf8");
  return JSON.parse(stripTrailingCommas(stripJsonComments(content)));
}
